/**
 * Cell Editor - Main component that selects the appropriate editor type
 */

import React from "react";
import CellEditorText from "./CellEditorText";
import CellEditorDropdown from "./CellEditorDropdown";
import CellEditorMultiSelect from "./CellEditorMultiSelect";
import CellEditorCheckbox from "./CellEditorCheckbox";
import { CellEditorProps, CellOption } from "./types";

export { CellEditorText, CellEditorDropdown, CellEditorMultiSelect, CellEditorCheckbox };
export type { CellEditorProps, CellOption };
export * from "./types";

const CellEditor: React.FC<CellEditorProps> = ({
  value,
  onChange,
  onSave,
  onCancel,
  isVisible,
  cellRect,
  cellWidth,
  editorType,
  fieldMetadata,
  options = [],
  isLoadingOptions = false,
  placeholder,
}) => {
  // Determine actual editor type from field metadata
  const resolvedEditorType = editorType || fieldMetadata?.editable || "text";

  // Check if multi-select based on field metadata
  const isMultiSelect = fieldMetadata?.multiSelect || resolvedEditorType === "multiselect";

  switch (resolvedEditorType) {
    case "dropdown":
      if (isMultiSelect) {
        return (
          <CellEditorMultiSelect
            value={Array.isArray(value) ? value : value ? [value] : []}
            onChange={onChange}
            onSave={onSave}
            onCancel={onCancel}
            isVisible={isVisible}
            cellRect={cellRect}
            cellWidth={cellWidth}
            options={options}
            isLoading={isLoadingOptions}
            placeholder={placeholder}
          />
        );
      }
      return (
        <CellEditorDropdown
          value={String(value || "")}
          onChange={onChange}
          onSave={onSave}
          onCancel={onCancel}
          isVisible={isVisible}
          cellRect={cellRect}
          cellWidth={cellWidth}
          options={options}
          isLoading={isLoadingOptions}
          placeholder={placeholder}
        />
      );

    case "multiselect":
      return (
        <CellEditorMultiSelect
          value={Array.isArray(value) ? value : value ? [value] : []}
          onChange={onChange}
          onSave={onSave}
          onCancel={onCancel}
          isVisible={isVisible}
          cellRect={cellRect}
          cellWidth={cellWidth}
          options={options}
          isLoading={isLoadingOptions}
          placeholder={placeholder}
        />
      );

    case "checkbox":
      return (
        <CellEditorCheckbox
          value={Boolean(value)}
          onChange={onChange}
          onSave={onSave}
          onCancel={onCancel}
          isVisible={isVisible}
          cellRect={cellRect}
          cellWidth={cellWidth}
        />
      );

    case "text":
    default:
      return (
        <CellEditorText
          value={String(value || "")}
          onChange={onChange}
          onSave={onSave}
          onCancel={onCancel}
          isVisible={isVisible}
          cellRect={cellRect}
          cellWidth={cellWidth}
          placeholder={placeholder}
        />
      );
  }
};

export default CellEditor;

